// SSN redaction for audit/history payloads (DEV-351). Masks to the app-wide
// display format ***-**-1234 (see client/src/lib/utils.ts maskSSN).

/** Keys that hold a personal SSN / taxpayer id. Matched case-insensitively. */
const SSN_KEY_RE = /ssn|social_?security|taxpayer_?id|^i?tin$/i;
const MASKED_RE = /^\*{3}-\*{2}-(\d{4}|\*{4})$/;
// 123-45-6789 / 123 45 6789 anywhere in free text
const FORMATTED_SSN_RE = /(?<!\d)\d{3}[- ]\d{2}[- ]\d{4}(?!\d)/g;
// Also bare 123456789 — only used where the field/text is known to be about SSNs
const ANY_SSN_RE = /(?<!\d)\d{3}[- ]?\d{2}[- ]?\d{4}(?!\d)/g;
const SSN_MENTION_RE = /ssn|social security/i;

const last4 = (m: string) => `***-**-${m.replace(/\D/g, "").slice(-4)}`;

export function isSsnKey(key: string): boolean {
  return SSN_KEY_RE.test(key);
}

/** Mask SSN-looking substrings. `ssnContext` = the value is known to be an SSN field. */
export function maskSsnInText(text: string, ssnContext = false): string {
  if (ssnContext) {
    if (MASKED_RE.test(text)) return text;
    const masked = text.replace(ANY_SSN_RE, last4);
    // Leftover digits outside a masked token (e.g. an 8-digit typo) -> hide entirely
    return /\d/.test(masked.replace(/\*{3}-\*{2}-\d{4}/g, "")) ? "***-**-****" : masked;
  }
  return text.replace(SSN_MENTION_RE.test(text) ? ANY_SSN_RE : FORMATTED_SSN_RE, last4);
}

/** ***-**-1234 for a stored SSN; null/undefined/"" pass through (DEV-351 follow-up). */
export function maskSsn<T extends string | number | null | undefined>(ssn: T): T | string {
  if (ssn == null || ssn === "") return ssn;
  const text = String(ssn);
  if (MASKED_RE.test(text)) return text;
  const digits = text.replace(/\D/g, "");
  return digits.length >= 4 ? `***-**-${digits.slice(-4)}` : "***-**-****";
}

/** True for a value that came from a masked API response — never a real SSN. */
export function isMaskedSsn(value: unknown): boolean {
  return typeof value === "string" && value.includes("*");
}

/**
 * Deep-copy `value` with every `ssn` field (employee / dependent rows) masked.
 * Only the exact `ssn` key is touched — ssnNotAvailable / ssnFollowUpDate stay as-is.
 */
export function maskSsnFields<T>(value: T): T {
  if (value == null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((v) => maskSsnFields(v)) as T;
  // Dates, Buffers etc. keep their own JSON serialization
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] =
      k === "ssn" && (typeof v === "string" || typeof v === "number")
        ? maskSsn(v)
        : maskSsnFields(v);
  }
  return out as T;
}

/** Copy of an update payload without `ssn` when it holds a masked value (so it can't overwrite the real one). */
export function dropMaskedSsn<T extends object>(payload: T): T {
  if (!payload || !isMaskedSsn((payload as any).ssn)) return payload;
  const { ssn: _ignored, ...rest } = payload as any;
  return rest as T;
}

/** Deep-copy `value` with every SSN masked. Strings that are JSON are parsed, masked and re-stringified. */
export function redactSsnDeep<T>(value: T, ssnContext = false): T {
  if (value == null) return value;
  if (typeof value === "number" || typeof value === "bigint") {
    return (ssnContext ? maskSsnInText(String(value), true) : value) as T;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.stringify(redactSsnDeep(JSON.parse(trimmed), ssnContext)) as T;
      } catch {
        // not JSON; fall through to text masking
      }
    }
    return maskSsnInText(value, ssnContext) as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => redactSsnDeep(v, ssnContext)) as T;
  }
  if (value instanceof Date) return value;
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactSsnDeep(v, ssnContext || isSsnKey(k));
    }
    return out as T;
  }
  return value;
}
